import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, Stack, Button } from "react-bootstrap";
import { getApplicationsList } from "@/services";
import {
  isActiveLoanStage,
  isInvoice,
  isRevenue,
  applicationPath,
} from "@/components/Layout/headerAccountDropdownUtils";

import "./dashboard_styles.css";

const getApplicationId = (app) => app?.uuid ?? app?.id;

const isActiveLoan = isActiveLoanStage;

/** LMS dashboard only after successful disbursal; otherwise LOS apply. */
const revenueLmsOrApplyTo = (app) =>
  isActiveLoan(app)
    ? `/lms/${getApplicationId(app)}/dashboard`
    : `/los/${getApplicationId(app)}/apply`;

/** Invoice platform only after successful disbursal; otherwise LOS apply. */
const invoicePlatformOrApplyTo = (app) =>
  isActiveLoan(app)
    ? `/invoice/${getApplicationId(app)}`
    : `/los/${getApplicationId(app)}/apply`;

/**
 * Resolves what to show after loading applications: auto-redirect (single-app rules),
 * landing cards (multi-app), or a simple list fallback.
 */
function resolveNavigatorView(apps) {
  const withId = apps.filter((a) => getApplicationId(a));
  const n = withId.length;

  if (n === 0) {
    return { kind: "empty" };
  }

  if (n === 1) {
    const app = withId[0];
    const id = getApplicationId(app);
    const canonical = applicationPath(app);
    if (canonical) {
      return { kind: "redirect", to: canonical };
    }
    return { kind: "redirect", to: `/los/${id}/apply` };
  }

  const activeLoanApps = withId.filter(isActiveLoan);
  const notActiveLoan = withId.filter((a) => !isActiveLoan(a));

  const invActive = activeLoanApps.find(isInvoice);
  const revActive = activeLoanApps.find(isRevenue);
  const invProg = notActiveLoan.find(isInvoice);
  const revProg = notActiveLoan.find(isRevenue);

  // 6 — Both products on active/closed loan and at least one application still in progress
  if (invActive && revActive && notActiveLoan.length >= 1) {
    const resumeApp = notActiveLoan[0];
    return {
      kind: "cards",
      cards: [
        {
          label: isActiveLoan(revActive)
            ? "Proceed to RBF loan dashboard"
            : "Resume RBF loan application",
          to: revenueLmsOrApplyTo(revActive),
        },
        {
          label: isActiveLoan(invActive)
            ? "Proceed to invoice discounting platform"
            : "Resume invoice discounting application",
          to: invoicePlatformOrApplyTo(invActive),
        },
        {
          label: "Resume loan application",
          to: `/los/${getApplicationId(resumeApp)}/apply`,
        },
      ],
    };
  }

  // 5 — Invoice active/closed + revenue either active/closed or in progress
  if (invActive && (revActive || revProg)) {
    const revenueTo =
      revActive != null ? revenueLmsOrApplyTo(revActive) : `/los/${getApplicationId(revProg)}/apply`;
    const revenueLabel =
      revActive != null
        ? isActiveLoan(revActive)
          ? "Proceed to RBF loan dashboard"
          : "Resume RBF loan application"
        : "Resume RBF loan application";
    return {
      kind: "cards",
      cards: [
        {
          label: revenueLabel,
          to: revenueTo,
        },
        {
          label: isActiveLoan(invActive)
            ? "Proceed to invoice discounting platform"
            : "Resume invoice discounting application",
          to: invoicePlatformOrApplyTo(invActive),
        },
      ],
    };
  }

  // 4 — Revenue active/closed + invoice application not yet active/closed
  if (revActive && invProg && !invActive) {
    return {
      kind: "cards",
      cards: [
        {
          label: "Resume loan application",
          to: `/los/${getApplicationId(invProg)}/apply`,
        },
        {
          label: isActiveLoan(revActive)
            ? "Proceed to Revenue-based financing"
            : "Resume RBF loan application",
          to: revenueLmsOrApplyTo(revActive),
        },
      ],
    };
  }

  // Revenue active/closed + another revenue application not yet active/closed (e.g. additional loan in LOS)
  if (revActive && revProg && !invActive && !invProg) {
    return { kind: "redirect", to: revenueLmsOrApplyTo(revActive) };
  }

  // 4 — Invoice active/closed + another non-active app that is not the “invoice + revenue” pair above
  if (invActive && notActiveLoan.length >= 1 && !revProg) {
    const resumeApp = notActiveLoan[0];
    return {
      kind: "cards",
      cards: [
        {
          label: "Resume loan application",
          to: `/los/${getApplicationId(resumeApp)}/apply`,
        },
        {
          label: isActiveLoan(invActive)
            ? "Proceed to invoice discounting platform"
            : "Resume invoice discounting application",
          to: invoicePlatformOrApplyTo(invActive),
        }
      ],
    };
  }

  const fallbackApp =
    withId.find(isActiveLoan) ?? withId.find(isRevenue) ?? withId.find(isInvoice) ?? withId[0];
  const fallbackPath = applicationPath(fallbackApp);
  return {
    kind: "redirect",
    to: fallbackPath ?? `/los/${getApplicationId(withId[0])}/apply`,
  };
}

/**
 * Navigator: loads applications and either redirects (single clear outcome),
 * shows action cards (multiple applications), or lists applications.
 */
export function Navigator() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchApplications = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getApplicationsList();
        if (cancelled) return;
        const list = response?.results ?? response?.data ?? [];
        const apps = Array.isArray(list) ? list : [];
        const resolved = resolveNavigatorView(apps);

        if (resolved.kind === "redirect") {
          navigate(resolved.to, { replace: true });
          return;
        }

        setApplications(apps);
        setView(resolved);
      } catch (err) {
        if (!cancelled) {
          console.error("Navigator: Error fetching applications", err);
          setError(err?.message ?? "Failed to load applications");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchApplications();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5" style={{ minHeight: "40vh" }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 mb-0 text-muted">Loading your applications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5" style={{ minHeight: "40vh" }}>
        <div className="text-center">
          <p className="text-danger mb-2">{error}</p>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => {
              setError(null);
              setLoading(true);
              getApplicationsList()
                .then((response) => {
                  const list = response?.results ?? response?.data ?? [];
                  const apps = Array.isArray(list) ? list : [];
                  const resolved = resolveNavigatorView(apps);
                  if (resolved.kind === "redirect") {
                    navigate(resolved.to, { replace: true });
                    return;
                  }
                  setApplications(apps);
                  setView(resolved);
                })
                .catch((e) => setError(e?.message ?? "Failed to load applications"))
                .finally(() => setLoading(false));
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (view?.kind === "cards") {
    return (
      <div className="container py-5 mt-4">
        <h2 className="m-4 text-center">Choose where to go next</h2>
        <Stack gap={3} className="mx-auto" style={{ maxWidth: 720 }}>
          {view.cards.map((card) => (
            <Card key={`${card.label}-${card.to}`} className="dashboardActionCard activeCard">
              <Stack direction="horizontal" gap={3} className="align-items-center justify-content-between flex-wrap w-100">
                <Stack gap={1} className="actionContent">
                  <h4 className="actionCardTitle h5">{card.label}</h4>
                </Stack>
                <Link to={card.to} className="text-decoration-none">
                  <Button variant="primary" className="actionButton">
                    Continue
                  </Button>
                </Link>
              </Stack>
            </Card>
          ))}
        </Stack>
      </div>
    );
  }

  return (
    <div className="container py-5 mt-4">
      <h2 className="mb-4">Your applications</h2>
      {applications.length === 0 || view?.kind === "empty" ? (
        <p className="text-muted">No applications yet.</p>
      ) : null}
    </div>
  );
}
